// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { EMPTY_GUID } from 'logic/data/Izenda.ReportsConstants';
import SubscriptionDetails from "./Izenda.SubscriptionDetails";

!(function() {
  require('share/utils/Izenda.StringUtilities');

  const {
    filter,
    isEmpty,
    every,
    some,
    forEach,
    map,
    isNil,
    flow,
    getOr,
    omit,
    get,
    union,
    toLower,
    join,
    split,
    tail,
    sortBy,
    concat,
    flatMap,
    includes,
    matchesProperty,
    isBoolean,
    find
  } = require('lodash/fp');

  var QuerySourceTree = require('./Izenda.QuerySourceTreeMultiLevel');

  var m = require('mobx');
  var constants = require('logic/data/Izenda.CopyManagementConstants');
  var trackable = require('share/tracking/Izenda.Trackable');
  var CopyManagementUtils = require('share/utils/Izenda.CopyManagementUtils');

  var WorkspaceTenant = require('logic/models/Izenda.WorkspaceTenant');
  var WorkspaceDetailCopyReport = require('./Izenda.WorkspaceDetailCopyReport');
  var WorkspaceMapping = require('logic/models/Izenda.WorkspaceMapping');
  var CategoryDetail = require('logic/models/Izenda.CategoryDetail');

  var commonMessages = require('share/messages/Izenda.CommonMessages');
  var DateFormatter = require('share/logging/common/DateFormatter').DateFormatter;
  var PagingModel = require('share/components/controls/DataGrid/helpers/Izenda.PagingModel');
  var localizer = require('share/localization/Izenda.Localizer');

  function WorkspaceDetail() {
    this.sourceReportsPrev = [];
    this.sourceTemplatesPrev = [];
    this.sourceDashboardsPrev = [];
    this.isLastPage = false;
    this.isLastPageOfDataModel = false;

    var props = {
      fakeReports: {},
      treeReports: [],
      fakeDataModels: {},
      treeDataModels: [],
      // State properties
      id: null,
      name: constants.DEFAULT_WORKSPACE_NAME,
      description: '',
      tenantId: null,
      ownerId: null,
      createdBy: null,
      created: null,
      createdAsString: new DateFormatter().formatUTCDate(new Date(), 'MM/dd/yyyy hh:mm:ss'),
      modified: null,
      copyOnlySettings: false,
      copyDataConnector: false,
      copyDataModel: false,
      copyAdvancedSettings: false,
      copySystemConfiguration: false,
      copyTenantPermissions: false,
      copyRoles: false,
      copyRolePermission: false,
      copyDashboard: false,
      copyReport: false,
      copyEmailConfiguration: false,
      copyReportConfiguration: false,
      copyGoogleMapConfiguration: false,
      mergeDuplicateMappings: false,
      sourceId: null,
      sourceHashCode: null,
      selectAllTenants: false,
      sourceDataModelJSON: null,
      isSelected: false,
      sourceConnections: [],
      workspaceTenants: [],
      workspaceMappings: [],

      // Temporary properties
      pageInfo: new PagingModel(constants.DESTINATION_SORT_DIR),
      alertMessage: commonMessages.EMPTY_MESSAGE,
      tenantListForDestination: [],
      originalTenantList: [],
      selectedDestinationTenantIDs: null,
      destinationGridData: [],
      destinationDropdownOpts: [],

      // selected databases/dataobjects from SourceTree used for Mappings grid
      selectedDatabases: [],
      selectedDataObjects: [],

      distinctConnections: [],
      // Use for Advanced Copy page
      selectedTenant: null,

      // Run Copy properties
      runCopyDataModels: [],
      conflictingReportTree: [],
      conflictingDashboardTree: [],
      copyOption: {},
      copyPositionId: false,
      copyFilterValues: false,
      copyAdvancedSettingOption: {},
      copyDashboardOption: {},
      copyReportOption: {},
      copyTemplateOption: {},
      copyTenantPermissionOption: {},
      copyRolePermissionOption: {},
      copiedRoles: [],
      copiedRolePermission: [],
      connectionConfirmation: [], // Run copy override confirmation
      canRunCopy: false,

      // All hash codes
      allHashcodes: {
        advancedSettings: '',
        tenantPermission: '',
        roles: '',
        rolePermissions: '',
        reports: '',
        templates: '',
        dashboards: ''
      },

      // copied report list
      sourceReports: {},
      sourceTemplates: {},
      sourceDashboards: {},

      // For filter
      reportFilterText: '',
      templateFilterText: '',
      dashboardFilterText: '',
      dataConnectorFilterText: '',

      // Computed properties
      querySourceTree: function() {
        return new QuerySourceTree(this.sourceConnections);
      },
      runCopyQuerySourceTree: function() {
        return new QuerySourceTree(this.runCopyDataModels);
      },
      isNew: function() {
        return !this.id;
      },
      haveDataSource: function() {
        return !isEmpty(this.sourceConnections);
      },
      needConfirmWhenLeaving: function() {
        return this.isDirty;
      },
      canSave: function() {
        return this.isDirty;
      },
      displaySourceConnections: function() {
        return map(function(connection) {
          connection.fullDBName = String.format('[{0}] {1}', connection.serverTypeName, connection.databaseName);
          connection.additionalData = {
            fullDBName: connection.fullDBName,
            databaseServer: connection.databaseServer, 
            databaseUser: connection.databaseUser
          };
          connection.fullDatabaseKey = String.format(
            '[{0}] {1} - {2} - {3}', 
            connection.serverTypeName, 
            connection.databaseName, 
            connection.databaseServer, 
            connection.databaseUser
          );
          return connection;
        }, this.sourceConnections);
      },
      showMappingGrid: function() {
        // Mapping grid is visible if and only if
        // the user selects an item in the Source and Destination(s) dropdown
        return this.selectedDestinationTenantIDs && this.sourceId;
      },

      // has at least one item is checked to copy
      hasItemToCopy: function() {
        return some(
          function(item) {
            return item;
          },
          [
            this.copyDataConnector,
            this.copyDataModel,
            this.copyAdvancedSettings,
            this.copyDashboard,
            this.copyReport,
            this.copyTenantPermissions,
            this.copyRoles,
            this.copyRolePermission,
            this.copySystemConfiguration
          ]
        );
      },

      activeRunCopy: function() {
        // no item is selected in Source and Destination(s) dropdown
        // no “items to copy” is selected
        // then Run Copy button is disabled.
        return !isNil(this.sourceId) && !isNil(this.selectedDestinationTenantIDs) && this.hasItemToCopy;
      },
      activeValidation: function() {
        // Data Model checkbox is selected in the Items to Copy section
        // AND At least a destination has status = Needs Validation
        return (
          (this.copyDataModel || this.copyReport || this.copyDashboard) && some({ status: 0 }, this.workspaceTenants)
        );
      },
      // Run copy roles & roles Permission
      roleList: [],
      allRoleCopyChecked: function() {
        return every({ checked: true }, this.roleList);
      },
      disableItemsToCopy: function() {
        return isNil(this.sourceId) || isNil(this.selectedDestinationTenantIDs);
      },
      isSystemSource: function() {
        return this.sourceId === constants.systemSource.id;
      },
      isSelectAllTenants: function() {
        return this.selectedDestinationTenantIDs === constants.systemSource.id;
      },
      isReadyToRunCopy: function() {
        // if click Ok in any conflicting pop up
        // then check selected item in there or not
        // if click on copy role permission
        // then check the role name selected
        // and check selected item in conflicting pop up
        return (
          (this.canRunCopy &&
            (this.checkHasSelectedConflictingItem(this.copyOption) ||
              this.checkHasSelectedConflictingItem(this.copyAdvancedSettingOption) ||
              this.checkHasSelectedConflictingItem(this.copyDashboardOption) ||
              this.checkHasSelectedConflictingItem(this.copyReportOption) ||
              this.checkHasSelectedConflictingItem(this.copyTemplateOption) ||
              this.checkHasSelectedConflictingItem(this.copyTenantPermissionOption) ||
              this.checkHasSelectedConflictingItem(this.copyRolePermissionOption))) ||
          (this.copyDataConnector) ||
          (this.copyDataModel && isEmpty(this.copyOption)) ||
          (this.copyAdvancedSettings && isEmpty(this.copyAdvancedSettingOption)) ||
          (this.copyDashboard && isEmpty(this.copyDashboardOption)) ||
          (this.copyReport && isEmpty(this.copyReportOption)) ||
          (this.copyReport && isEmpty(this.copyTemplateOption)) ||
          (this.copyTenantPermissions && isEmpty(this.copyTenantPermissionOption)) ||
          (this.copyRoles && !isEmpty(this.copiedRoles)) ||
          (this.copyRolePermission &&
            !isEmpty(this.copiedRolePermission) &&
            (this.checkHasSelectedConflictingItem(this.copyRolePermissionOption) ||
              isEmpty(this.copyRolePermissionOption))) ||
          (this.copySystemConfiguration)
        );
      },

      // copy report
      selectedDatabaseForCopiedReport: function() {
        return filter(function(connection) {
          CopyManagementUtils.updateDataSoucreBasedOnLinkedReport(connection);
          return connection.belongToCopiedReport;
        }, this.sourceConnections);
      },

      // copy data connectors
      selectedDataConnectors: function() {
        return filter(connection => connection.copyDataConnector, this.sourceConnections);
      },

      // mapping: list of connections
      mapppingDatabasesSource: function() {
        let databases = this.selectedDatabases;
        databases = union(databases, this.selectedDatabaseForCopiedReport);
        databases = union(databases, this.selectedDataConnectors);
        return databases;
      },

      // mapping: list of objects
      mapppingDataObjects: function() {
        var mapppingDatabasesSource = this.mapppingDatabasesSource;
        var re = {};

        forEach(function(connection) {
          re[connection.fullDatabaseKey] = flow(getOr([], 'dbSource.querySources'), filter(filterDataObject))(connection);
        }, mapppingDatabasesSource);

        return re;

        function filterDataObject(schema) {
          return (
            schema.checked || schema.belongToCopiedReport // for copy data model
          ); // for copy report/template
        }
      }
    };

    m.extendObservable(trackable(this), props);
  }

  /**
   * workspace detail validation constraints
   * @type {Object}
   */
  WorkspaceDetail.constraints = {
    name: constants.WORKSPACE_NAME_CONSTRAINT
  };

  /**
   * Read workspace detail data (mostly this data comes from API).
   * @param  {Object} detail
   */
  WorkspaceDetail.prototype.readDetail = m.action(function(detail) {
    this.id = detail.id;
    this.name = detail.name;
    this.description = detail.description;
    this.tenantId = detail.tenantId;
    this.ownerId = detail.ownerId;
    this.createdBy = detail.createdBy;
    this.created = detail.created;
    this.modified = detail.modified;
    this.copyDataConnector = detail.copyDataConnector;
    this.copySystemConfiguration = detail.copySystemConfiguration;
    this.copyDataModel = detail.copyDataModel;
    this.copyPositionId = detail.copyPositionId;
    this.copyFilterValues = detail.copyFilterValues;

    // adjustment of data received from the server (IZ-26401)
    const copyRoles = detail.copyRoles || detail.copyRolePermission;
    this.copyRoles = copyRoles;

    const isCopyRolesInactiveOrEmpty = !detail.copyRoles || isEmpty(detail.copiedRoles);
    const isCopiedRolesHasUniqueItems = !isCopyRolesInactiveOrEmpty && some(function(copiedRole) {
      return detail.copiedRolePermission.indexOf(copiedRole) === -1;
    }, detail.copiedRoles);
    const copyRolePermissions = detail.copyRolePermission
      && !isCopiedRolesHasUniqueItems;
    this.copyRolePermission = copyRolePermissions;

    const copiedRoles = detail.copiedRoles || [];
    detail.copyRolePermission && forEach(function(roleId) {
      if(copiedRoles.indexOf(roleId) === -1){
        copiedRoles.push(roleId);
      }
    }, detail.copiedRolePermission);
    this.copiedRoles = copiedRoles;

    this.copiedRolePermission = copyRolePermissions ? detail.copiedRolePermission : [];

    this.copyOnlySettings = detail.copyOnlySettings;
    this.copyAdvancedSettings = detail.copyAdvancedSettings;
    this.copyTenantPermissions = detail.copyTenantPermissions;
    this.copyDashboard = detail.copyDashboard;
    this.copyReport = detail.copyReport;
    this.mergeDuplicateMappings = detail.mergeDuplicateMappings;
    this.sourceId = detail.sourceId;
    this.sourceHashCode = detail.sourceHashCode;
    this.selectAllTenants = detail.selectAllTenants;
    this.sourceDataModelJSON = detail.sourceDataModelJSON;
    this.copyGoogleMapConfiguration = detail.copyGoogleMapConfiguration;
    this.copyEmailConfiguration = detail.copyEmailConfiguration;
    this.copyReportConfiguration = detail.copyReportConfiguration;

    this.workspaceTenants = map(function(tenant) {
      return new WorkspaceTenant(tenant);
    }, detail.workspaceTenants);

    this.readSchedule(detail.schedule);

    this.readSourceConnections(detail.sourceConnections);

    this.setSourceReports(detail.sourceReports, detail.sourceTemplates);
    this.setSourceDashboards(detail.sourceDashboards);

    this.workspaceMappings = map(
      function(mappingItem) {
        return new WorkspaceMapping(mappingItem, this.mapppingDatabasesSource);
      }.bind(this),
      detail.workspaceMappings
    );
    const allHashcodes = detail.allHashcodes;
    this.allHashcodes = {
      advancedSettings: !isNil(allHashcodes) ? allHashcodes.advancedSettings : '',
      tenantPermission: !isNil(allHashcodes) ? allHashcodes.tenantPermission : '',
      roles: !isNil(allHashcodes) ? allHashcodes.roles : '',
      rolePermissions: !isNil(allHashcodes) ? allHashcodes.rolePermissions : '',
      dashboards: !isNil(allHashcodes) ? allHashcodes.dashboards : '',
      reports: !isNil(allHashcodes) ? allHashcodes.reports : '',
      templates: !isNil(allHashcodes) ? allHashcodes.templates : ''
    };
    this.resetDirty();

    return Promise.resolve();
  });

  /**
   * [loadInitialData description]
   * @return {[type]} [description]
   */
  WorkspaceDetail.prototype.loadInitialData = m.action(function() {
    this.updateDataForDestination(this.sourceId);

    // calculate selected Databases/DataObjects for Mappings grid
    this.calcSelectedDatabases();
    this.calcSelectedDataObjects();

    if (isEmpty(this.workspaceTenants) && this.selectAllTenants) {
      this.updateSelectedTenantsForDestination(constants.systemSource.id);
    } else if (!isEmpty(this.workspaceTenants)) {
      var selectedTenant = [];
      forEach(function(item) {
        selectedTenant.push(item.tenantId);
      }, this.workspaceTenants);
      selectedTenant = join(',', selectedTenant);
      this.updateSelectedTenantsForDestination(selectedTenant);
    }
    this.workspaceTenants.forEach(function(item) {
      item.name = item.tenantName;
      item.tenantName = String.format('{0} - {1}', item.tenantUniqueName, item.tenantName);
    });
  });

  /**
   * Read data sources (mostly this data comes from API)
   * @param  {Object} dbSource
   */
  WorkspaceDetail.prototype.readSourceConnections = m.action(function(sourceConnections) {
    this.sourceConnections = sourceConnections || [];
    this.reCalculateSelectedSources();
  });

  /**
   * Read schedule from raw API data
   * @param {Object} schedule
   */
  WorkspaceDetail.prototype.readSchedule = m.action(function(schedule) {
    this.copySchedule = null;
    if (!isNil(schedule)) {
      const scheduleData = new SubscriptionDetails();
      scheduleData.populate(schedule);
      this.copySchedule = scheduleData;
    }
  });

  /**
   * Read distinct connection
   */
  WorkspaceDetail.prototype.readDistinctConnections = m.action(function(connectionItems) {
    var displayConnections = map(function(connectionItem) {
      const connection = connectionItem.value;
      connection.fullDBName = String.format('[{0}] {1}', connection.serverTypeName, connection.databaseName);
      connection.additionalData = {
        fullDBName: connection.fullDBName,
        databaseServer: connection.databaseServer,
        databaseUser: connection.databaseUser
      };
      connection.distinctKey = connectionItem.key;
      connection.fullDatabaseKey = String.format(
        '[{0}] {1} - {2} - {3}',
        connection.serverTypeName,
        connection.databaseName,
        connection.databaseServer,
        connection.databaseUser
      );
      return connection;
    }, connectionItems);

    this.distinctConnections = displayConnections;
  });

  /**
   * Set AlertMessage
   */
  WorkspaceDetail.prototype.setAlertMessage = function(newAlertMessage) {
    this.alertMessage = newAlertMessage;
  };

  /**
   * Set Copy PositionId
   * @param {any} value
   */
  WorkspaceDetail.prototype.setCopyPositionId = function(value) {
    this.copyPositionId = value;
    this.markDirty();
  };

  /**
   * Set Copy Filter Values
   * @param {any} value
   */
  WorkspaceDetail.prototype.setCopyFilterValues = function(value) {
    this.copyFilterValues = value;
    this.markDirty();
  };

  /**
   * Set property value
   * @param {type} property
   * @param {type} value
   */
  WorkspaceDetail.prototype.setPropertyValue = function(property, value) {
    // Reset alert message
    this.setAlertMessage(commonMessages.EMPTY_MESSAGE);
    this[property] = value;
    this.markDirty();
  };

  /**
   * [getOriginalTenantList generate original tenant list and change 'System' to 'All Tenants']
   * @param  {[Array]} options [description]
   * @return {[Array]}         [description]
   */
  WorkspaceDetail.prototype.getOriginalTenantList = m.action(function(options) {
    this.originalTenantList = m.toJS(options) || [];

    !isEmpty(this.originalTenantList) &&
      forEach(function(item) {
        if (item.id === constants.systemSource.id) {
          item.name = localizer.getText('COPY_MANAGEMENT_WORKSPACE_DETAIL_LABEL_ALL_TENANTS');
        }
      }, this.originalTenantList);
    this.loadInitialData();

    return Promise.resolve();
  });

  /**
   * create new Workspace Tenant from DB object
   * @param  {Object} detail
   * @return {Object} detail
   */
  WorkspaceDetail.prototype.newWorkspaceTenant = function(detail) {
    var tenant = new WorkspaceTenant({
      tenantId: detail.id,
      tenantName: detail.name,
      name: detail.tenantName,
      workspaceId: detail.workspaceId,
      tenantUniqueName: detail.tenantUniqueName,
      status: detail.status,
      state: detail.state,
      inserted: detail.inserted,
      version: detail.version,
      created: detail.created,
      createdBy: detail.createdBy,
      modified: detail.modified,
      modifiedBy: detail.modifiedBy,
      destinationConnections: detail.destinationConnections,
      sourceConnections: detail.sourceConnections,
      dataMapping: detail.dataMapping
    });

    return tenant;
  };

  /**
   * Update data selected tenant for Dropdownlist and datasource in Destination Grid
   * @param  {String} tenantId
   * @return {Promise}
   */
  WorkspaceDetail.prototype.updateSelectedTenantsForDestination = m.action(function(tenantId, sourceId) {
    sourceId = sourceId || '';

    if (tenantId) {
      this.selectedDestinationTenantIDs = tenantId;
      var tenantIdList = split(',', this.selectedDestinationTenantIDs);
      var allTenant = find(function(item) {
        return item === constants.systemSource.id;
      }, tenantIdList);

      if (this.selectAllTenants === true || allTenant) {
        forEach(
          function(tenant) {
            !find({ tenantId: tenant.id }, this.workspaceTenants) &&
              this.workspaceTenants.push(this.newWorkspaceTenant(tenant));
          }.bind(this),
          tail(this.destinationDropdownOpts)
        );

        this.workspaceTenants = allTenant
          ? sortBy(function(tenant) {
              return toLower(tenant.tenantName);
            }, this.workspaceTenants)
          : this.workspaceTenants;
        this.selectedDestinationTenantIDs = constants.systemSource.id;
        this.selectAllTenants = true;
      } else {
        /**
         * when add new tenant in dropdown list
         * find item is not belong to data source grid and push it
         */
        this.workspaceTenants = filter(
          function(item) {
            return includes(item.tenantId, tenantIdList) && item.tenantId != sourceId;
          }.bind(this),
          this.workspaceTenants
        );

        /**
         * remove source item in tenantIds
         */
        this.selectedDestinationTenantIDs = this.selectedDestinationTenantIDs.replace(sourceId.toString(), '');

        /**
         * remove the tenant in dropdown list
         * then remove the workspace in datasource grid
         */
        forEach(
          function(itemData) {
            var tenant = find({ id: itemData }, this.destinationDropdownOpts);
            var workspace = find({ tenantId: itemData }, this.workspaceTenants);
            if (tenant && !workspace) {
              this.workspaceTenants.push(this.newWorkspaceTenant(tenant));
            }
          }.bind(this),
          tenantIdList
        );
      }
    } else {
      this.resetDataForDestination();
    }

    return Promise.resolve();
  });

  /**
   * reset data for Destination
   */
  WorkspaceDetail.prototype.resetDataForDestination = m.action(function() {
    this.selectedDestinationTenantIDs = null;
    this.workspaceTenants = [];
    this.selectAllTenants = false;
    this.markDirty();
  });

  /**
   * [updateDataForDestination this function is to update the selected value in source dropdown]
   * @param  {[string]} sourceId
   */
  WorkspaceDetail.prototype.updateDataForDestination = m.action(function(sourceId) {
    this.destinationDropdownOpts = isNil(sourceId)
      ? this.originalTenantList
      : filter(
          function(item) {
            return item.id !== sourceId || item.id === constants.systemSource.id;
          }.bind(this),
          this.originalTenantList
        );
  });

  /**
   * [onSortChange update sort infor]
   * @param  {[string]} columnKey
   * @param  {[object]} sortDir
   */
  WorkspaceDetail.prototype.onSortChange = function(columnKey, sortDir) {
    this.pageInfo.setColSortDirs(columnKey, sortDir);
  };

  /**
   * [updateOwner ]
   * @param  {[string]} ownerId [description]
   */
  WorkspaceDetail.prototype.updateOwnerList = function(ownerId) {
    this.ownerId = ownerId;
  };

  /**
   *  Update errorMessages
   */
  WorkspaceDetail.prototype.showAlertMessage = function(type, message) {
    this.alertMessage = {
      type: type,
      message: message
    };
    return null;
  };

  /**
   *  Update Workspace name
   */
  WorkspaceDetail.prototype.updateWorkspaceName = m.action(function(name) {
    this.name = name;
    this.markDirty();
  });

  /**
   * Update Workspace description
   */
  WorkspaceDetail.prototype.updateWorkspaceDescription = function(description) {
    this.description = description;
  };

  /**
   * Re-calculate selected databases/dataobjects of DataSourceTree,
   * then re-update sourceConnections for all tenants when master SourceTree changes
   */
  WorkspaceDetail.prototype.reCalculateSelectedSources = m.action(function() {
    this.calcSelectedDataConnectors();
    this.calcSelectedDatabases();
    this.calcSelectedDataObjects();
    this.updateWorkspaceTenants();
  });

  /**
   * calc selected data connectors
   */
  WorkspaceDetail.prototype.calcSelectedDataConnectors = function() {
    if(!this.copyDataConnector){
      forEach(x => {
        x.copyDataConnector = false;
      }, this.sourceConnections);
    }
  };

  /**
   * calc selected Databases of Source Tree
   */
  WorkspaceDetail.prototype.calcSelectedDatabases = function() {
    this.selectedDatabases = this.displaySourceConnections.filter(matchesProperty('checked', true));
  };

  /**
   * calc selected Data Objects of Source Tree
   */
  WorkspaceDetail.prototype.calcSelectedDataObjects = function() {
    var matchedArray = [];

    map(function(connection) {
      // filter checked Data Objects
      var checkedDataObjects = filter({ checked: true }, connection.dbSource.querySources);
      matchedArray = concat(checkedDataObjects, matchedArray);
    }, this.sourceConnections);

    this.selectedDataObjects = matchedArray;
  };

  /**
   * re-update sourceConnections for all tenants when master SourceTree changes
   */
  WorkspaceDetail.prototype.updateWorkspaceTenants = m.action(function() {
    map(
      function(tenant) {
        tenant.readSourceConnections(this.mapppingDatabasesSource);
      }.bind(this),
      this.workspaceTenants
    );
  });

  /**
   * Set selected tenant to display advanced copy
   */
  WorkspaceDetail.prototype.setSelectedTenant = m.action(function(tenant, databaseSources) {
    var sourceConnections =
      tenant && !isEmpty(tenant.sourceConnections) ? tenant.sourceConnections : this.mapppingDatabasesSource;
    var tenantMapping = this.workspaceMappings.filter(
      function(mapping) {
        return (
          tenant &&
          !!find(
            {
              tenantId: tenant.tenantId
            },
            mapping.workspaceMappingTenants
          )
        );
      }.bind(this)
    );

    tenantMapping = tenantMapping.map(
      function(mapping) {
        var newMapping = new WorkspaceMapping(mapping, this.mapppingDatabasesSource, databaseSources);
        newMapping.workspaceMappingTenants = filter(function(mappingTenant) {
          return mappingTenant.tenantId === tenant.tenantId;
        }, newMapping.workspaceMappingTenants);

        return newMapping;
      }.bind(this),
      tenantMapping
    );

    this.selectedTenant = tenant
      ? new WorkspaceTenant(tenant, tenantMapping, databaseSources, sourceConnections)
      : null;

    // Reset alert message
    this.setAlertMessage(commonMessages.EMPTY_MESSAGE);
    return Promise.resolve();
  });

  /**
   * Update work space tenant from advanced copy to master page
   */
  WorkspaceDetail.prototype.updateWorkspaceTenant = m.action(function() {
    var selectedTenant = this.selectedTenant;

    // Find editing workspace tenant first
    var editTenant = find({ tenantId: selectedTenant.tenantId }, this.workspaceTenants);
    // Populate workspace data
    editTenant.poppulateData(selectedTenant);

    // Split mapping item
    var deleteMappings = this.splitMapping();

    for (var i = deleteMappings.length - 1; i >= 0; i--) {
      this.workspaceMappings.splice(deleteMappings[i], 1);
    }

    // Merge mappings from advanced copy to workspace mappings
    var newMappings = this.mergeMapping();
    this.workspaceMappings = this.workspaceMappings.concat(newMappings);

    // Back to main screen
    this.selectedTenant = null;
  });

  /**
   * Merge mapping from advanced copy to global mapping
   */
  WorkspaceDetail.prototype.mergeMapping = m.action(function() {
    var selectedTenant = this.selectedTenant;
    var advancedMappings = selectedTenant.dataMapping;
    var newMappings = [];

    forEach(
      function(advancedMapping) {
        var matchMapping = filter(function(workspaceMapping) {
          return workspaceMapping.compare(advancedMapping, selectedTenant);
        }, this.workspaceMappings);

        // If match mappings have tenant equal  selected tenant
        var matchSelectedTenant = find(function(mapping) {
          return !!find({ tenantId: selectedTenant.tenantId }, mapping.workspaceMappingTenants);
        }, matchMapping);
        if (matchSelectedTenant) {
          if (matchSelectedTenant.workspaceMappingTenants.length === 1) {
            matchSelectedTenant.toDatabaseName = advancedMapping.toDatabaseName;
            matchSelectedTenant.toObject = advancedMapping.toObject;
          }
          return;
        }

        // Merge to the first item
        if (matchMapping.length > 0 && matchMapping[0]) {
          var workspaceMappingTenant = matchMapping[0].workspaceMappingTenants;
          var existedSelectedTenant = find({ tenantId: selectedTenant.tenantId }, workspaceMappingTenant);
          !existedSelectedTenant && matchMapping[0].addMappingTenant(selectedTenant.tenantId);
        } else {
          advancedMapping.addMappingTenant(selectedTenant.tenantId);
          newMappings.push(new WorkspaceMapping(advancedMapping, this.mapppingDatabasesSource));
        }
      }.bind(this),
      advancedMappings
    );

    return newMappings;
  });

  /**
   * Split mapping from advanced copy to global mapping
   */
  WorkspaceDetail.prototype.splitMapping = m.action(function() {
    var selectedTenant = this.selectedTenant;
    var workspaceMappings = this.workspaceMappings;
    var advancedMappings = selectedTenant.dataMapping;
    var deleteMappings = [];

    var globalMappings = workspaceMappings.filter(function(mapping) {
      return selectedTenant && !!find({ tenantId: selectedTenant.tenantId }, mapping.workspaceMappingTenants);
    });

    forEach(function(globalMapping) {
      var existMapping = find(function(advancedMapping) {
        return globalMapping.compare(advancedMapping, selectedTenant);
      }, advancedMappings);

      // If not exist, remove this tenant in list tenant
      if (!existMapping) {
        var globalMappingTenants = globalMapping.workspaceMappingTenants;
        var removedTenant = find({ tenantId: selectedTenant.tenantId }, globalMapping.workspaceMappingTenants);
        if (removedTenant && globalMappingTenants.length === 1) {
          var globalMappingIndex = workspaceMappings.indexOf(globalMapping);
          deleteMappings.push(globalMappingIndex);
        } else if (removedTenant) {
          var removeIndex = globalMappingTenants.indexOf(removedTenant);
          globalMappingTenants.splice(removeIndex, 1);
        }
      }
    }, globalMappings);

    return deleteMappings;
  });

  /**
   * Add new workspace mapping
   */
  WorkspaceDetail.prototype.addWorkspaceMapping = function() {
    var newMappingItem = new WorkspaceMapping();

    this.workspaceMappings.push(newMappingItem);
  };

  /**
   * Get data for saving Workspace Detail
   */
  WorkspaceDetail.prototype.getDataForSave = function(isSaveAs) {
    var cloneWorkspaceTenants = m.toJS(this.workspaceTenants);
    var schedule = m.toJS(this.copySchedule) || null;
    if (schedule)
      if (!schedule.id || schedule.id.indexOf('newInstance_') === 0)
        schedule.id = EMPTY_GUID;
    var workspaceDetails = {
      workspace: {
        id: isSaveAs ? null : this.id,
        name: this.name,
        description: this.description,
        tenantId: this.tenantId,
        ownerId: this.ownerId,
        created: this.created || null,
        createdBy: this.createdBy,
        deleted: this.deleted,
        modified: this.modified,
        modifiedBy: this.modifiedBy,
        copyDataModel: this.copyDataModel,
        copyPositionId: this.copyDataModel ? this.copyPositionId : false,
        copyFilterValues: this.copyReport || this.copyDashboard ? this.copyFilterValues : false,
        copyRoles: this.copyRoles,
        copyRolePermission: this.copyRolePermission,
        copyAdvancedSettings: this.copyAdvancedSettings,
        copyTenantPermissions: this.copyTenantPermissions,
        copyDashboard: this.copyDashboard,
        copyReport: this.copyReport,
        copyDataConnector: this.copyDataConnector,
        schedule: schedule,
        copySystemConfiguration: this.copySystemConfiguration,
        mergeDuplicateMappings: this.mergeDuplicateMappings,
        sourceId: this.sourceId,
        sourceHashCode: this.sourceHashCode,
        state: this.state || 0,
        selectAllTenants: this.selectAllTenants,
        workspaceTenants: cloneWorkspaceTenants.map(omit(['sourceConnections', 'destinationConnections'])),
        workspaceMappings: this.workspaceMappings,
        copyOption: this.copyOption,
        copyOnlySettings: this.copyOnlySettings,
        copyAdvancedSettingOption: this.copyAdvancedSettingOption,
        copyDashboardOption: this.copyDashboardOption,
        copyReportOption: this.copyReportOption,
        copyTemplateOption: this.copyTemplateOption,
        copyTenantPermissionOption: this.copyTenantPermissionOption,
        copyRolePermissionOption: this.copyRolePermissionOption,
        copiedRoles: this.copyRoles ? this.copiedRoles : [],
        copiedRolePermission: this.copyRolePermission ? this.copiedRolePermission : [],
        copyEmailConfiguration: this.copyEmailConfiguration,
        copyGoogleMapConfiguration: this.copyGoogleMapConfiguration,
        copyReportConfiguration: this.copyReportConfiguration,
        allHashcodes: {
          advancedSettings: this.allHashcodes.advancedSettings || '',
          tenantPermission: this.allHashcodes.tenantPermission || '',
          roles: this.allHashcodes.roles || '',
          rolePermissions: this.allHashcodes.rolePermissions || '',
          dashboards: this.allHashcodes.dashboards || '',
          reports: this.allHashcodes.reports || '',
          templates: this.allHashcodes.templates || ''
        },
        sourceDashboards: this.copyDashboard ? this.sourceDashboards.categories : []
      },
      selectedSources: this.omitProperties(this.mapppingDatabasesSource, 'querySourceFields')
    };

    // data for copy reports (user reports & templates)
    if (this.copyReport) {
      workspaceDetails.workspace.sourceReports = m.toJS(get('categories', this.sourceReports));
      workspaceDetails.workspace.sourceTemplates = m.toJS(get('categories', this.sourceTemplates));
    }

    return workspaceDetails;
  };

  /**
   * [getAllDestinationConnection description]
   * @return {Array}
   */
  WorkspaceDetail.prototype.getAllDestinationConnection = function() {
    var destination = [];
    forEach(
      function(workspace) {
        forEach(function(connection) {
          destination.push(connection.id);
        }, workspace.destinationConnections);
      }.bind(this),
      this.workspaceTenants
    );

    return destination;
  };

  /**
   * get matched SourceTree For Run Copy Popup
   * @return {Array} QuerySourceTree
   */
  WorkspaceDetail.prototype.getMatchedSourceTreeForRunCopy = m.action(function(result, isCopyDataModel) {
    var runCopyDataModels = m.toJS(
      forEach(function(tenant) {
        return forEach(function(destination) {
          return result.indexOf(destination.id) >= 0;
        }, tenant);
      }, this.workspaceTenants)
    );
    // remove tenant with status different with Ready to copy
    if (isCopyDataModel) {
      runCopyDataModels = filter(function(tenant) {
        return tenant.status === constants.STATUS_OF_DESTINATION.READY_TO_COPY;
      }, runCopyDataModels);
    }

    // Set source tree for tenant
    runCopyDataModels.forEach(function(tenant) {
      var querySoure = find({ id: tenant.tenantId }, result);
      if (querySoure) {
        tenant.querySources = querySoure.roles;
      } else {
        tenant.querySources = tenant.destinationConnections;
      }
      tenant.checked = true;
      tenant.name = tenant.tenantName;
      tenant.querySources.forEach(function(querySource) {
        querySource.checked = true;
      });
    });

    // build the data model for run copy source tree
    this.runCopyDataModels = [
      {
        id: 'all',
        name: 'All',
        checked: true,
        dbSource: {
          querySources: runCopyDataModels
        }
      }
    ];

    return this.runCopyQuerySourceTree;
  });

  /**
   * [getSourceTreeTenantForRunCopy description]
   * @param  {[type]} result [description]
   * @return {[type]}        [description]
   */
  WorkspaceDetail.prototype.getSourceTreeTenantForRunCopy = m.action(function(result) {
    var runCopyDataModels = m.toJS(this.workspaceTenants);

    // Set source tree for tenant
    runCopyDataModels.forEach(function(tenant) {
      if (result.indexOf(tenant.tenantId) >= 0) {
        tenant.checked = true;
        tenant.name = tenant.tenantName;
      }
    });

    // build the data model for run copy source tree
    this.runCopyDataModels = [
      {
        id: 'all',
        name: 'All',
        checked: true,
        dbSource: {
          querySources: runCopyDataModels
        }
      }
    ];

    return this.runCopyQuerySourceTree;
  });

  /**
   * Get conflicting report tree
   * @param {Object} result
   */
  WorkspaceDetail.prototype.getConflictingDashboardTree = m.action(function(result) {
    this.conflictingDashboardTree = !isEmpty(result)
      ? {
          name: 'All',
          id: 'All',
          checked: true,
          indeterminate: false,
          tenants: map(getTenants, result)
        }
      : [];

    function getTenants(tenant) {
      return {
        name: tenant.tenantName,
        id: tenant.tenantId,
        checked: tenant.checked,
        indeterminate: false,
        categories: map(function(category) {
          return new CategoryDetail(category, true);
        }, tenant.categories)
      };
    }

    return this.conflictingDashboardTree;
  });

  /**
   * Get conflicting report tree
   * @param {Object} result
   */
  WorkspaceDetail.prototype.getConflictingReportTree = m.action(function(result) {
    this.conflictingReportTree = !isEmpty(result)
      ? {
          name: 'All',
          id: 'All',
          checked: true,
          indeterminate: false,
          tenants: map(getTenants, result)
        }
      : [];

    function getTenants(tenant) {
      return {
        name: tenant.tenantName,
        id: tenant.tenantId,
        checked: tenant.checked,
        indeterminate: false,
        categories: map(function(category) {
          return new CategoryDetail(category);
        }, tenant.categories)
      };
    }

    return this.conflictingReportTree;
  });

  /**
   * omit properties out of dataTree
   * @param [Array]  dataTree
   * @param {String} iteratee
   * @return [Array]
   */
  WorkspaceDetail.prototype.omitProperties = function(selectedDatabases, iteratee) {
    var cloneArr = m.toJS(selectedDatabases);
    cloneArr.map(function(connection) {
      delete connection.querySources;
      nestedOmit(connection.dbSource.querySources);
    });

    function nestedOmit(querySources) {
      map(function(source, index) {
        delete source[iteratee];
        !isEmpty(source.querySources) && nestedOmit(source.querySources);
      }, querySources);
    }
    return cloneArr;
  };

  /**
   * Reset selected tenant
   */
  WorkspaceDetail.prototype.resetSelectedTenant = function() {
    this.selectedTenant = null;
  };

  /**
   * get Data Model For RunCopy
   * @return {[type]}
   */
  WorkspaceDetail.prototype.getDataModelForRunCopy = m.action(function(itemToCopy, onlyGetTenant, selected) {
    forEach(
      function(tenant) {
        if (onlyGetTenant) {
          this[itemToCopy][tenant.tenantId] = isBoolean(selected) ? selected : tenant.checked;
        } else {
          forEach(
            function(querySource) {
              this[itemToCopy][querySource.id] = isBoolean(selected) ? selected : querySource.checked;
            }.bind(this),
            tenant.querySources
          );
        }
      }.bind(this),
      this.runCopyDataModels[0].dbSource.querySources
    );
  });

  /**
   * Get dashboard data for run copy
   * @param {String} option
   * @param {Boolean} cancel
   */
  WorkspaceDetail.prototype.getDataDashboardsForRunCopy = m.action(function(cancel) {
    this.copyDashboardOption = {};
    forEach(
      function(tenant) {
        forEach(
          function(category) {
            forEach(
              function(subCategory) {
                forEach(
                  function(dashboard) {
                    this.copyDashboardOption[dashboard.id] = !isNil(cancel) ? false : dashboard.checked;
                  }.bind(this),
                  subCategory.dashboards
                );
              }.bind(this),
              category.subCategories
            );
          }.bind(this),
          tenant.categories
        );
      }.bind(this),
      this.conflictingDashboardTree.tenants
    );
  });

  /**
   * Get report data for run copy
   * @param {String} option
   */
  WorkspaceDetail.prototype.getDataReportsForRunCopy = m.action(function(option, cancel) {
    this[option] = {};
    forEach(
      function(tenant) {
        forEach(
          function(category) {
            forEach(
              function(subCategory) {
                forEach(
                  function(report) {
                    this[option][report.id] = !isNil(cancel) ? false : report.checked;
                  }.bind(this),
                  subCategory.reports
                );
              }.bind(this),
              category.subCategories
            );
          }.bind(this),
          tenant.categories
        );
      }.bind(this),
      this.conflictingReportTree.tenants
    );
  });

  /**
   * Find Selected database
   */
  WorkspaceDetail.prototype.findSelectedDatabase = function(fullDatabaseKey) {
    var selectedDB = find({ fullDatabaseKey: fullDatabaseKey }, this.mapppingDatabasesSource);

    return selectedDB;
  };

  /**
   * Find Selected source connection
   */
  WorkspaceDetail.prototype.findSelectedConnection = function(fullDatabaseKey) {
    var selectedTenant = this.selectedTenant || {};
    var selectedConnection = find({ fullDatabaseKey: fullDatabaseKey }, selectedTenant.databaseSources);

    return selectedConnection;
  };

  /**
   * Delete Destination tenant
   */
  WorkspaceDetail.prototype.deleteDestinationTenant = m.action(function(tenant) {
    var deletedIndex = this.workspaceTenants.indexOf(tenant);

    this.workspaceTenants.splice(deletedIndex, 1);

    // Remove item in selected destination
    this.selectedDestinationTenantIDs = flatMap(function(tenant) {
      return tenant.tenantId;
    }, this.workspaceTenants).toString();
    this.selectAllTenants = false;
  });

  /**
   * set sourceId
   */
  WorkspaceDetail.prototype.setSource = m.action(function(sourceId) {
    this.sourceId = sourceId;
    if (this.isSystemSource) {
      this.copyTenantPermissions = false;
      this.copyRoles = false;
    }

    this.resetDataSourceForCopy();
  });

  /**
   * populate Roles And Permissions
   * @param  {object}  result
   * @param  {Boolean} isLoadRoles
   */
  WorkspaceDetail.prototype.populateRolesAndPermissions = function(result) {
    this.roleList = map(
      function(role) {
        var savedRole = !!find(function(roleId) {
          return roleId === role.id;
        }, this.copiedRoles);
        return {
          id: role.id,
          name: role.name,
          checked: savedRole
        };
      }.bind(this),
      result.data
    );

    this.allHashcodes.roles = result.roleHashcode || '';
    this.allHashcodes.rolePermissions = result.permissionHashcode || '';
  };

  /**
   * select All Role
   * @param  {Boolean}  value
   */
  WorkspaceDetail.prototype.selectAllRole = m.action(function(value) {
    this.copiedRoles = [];
    this.roleList.forEach(
      function(role) {
        role.checked = value;
        value && this.copiedRoles.push(role.id);
      }.bind(this)
    );
  });

  /**
   * selected Role Copy
   * @param  {string} tenantId
   * @param  {Boolean} value
   */
  WorkspaceDetail.prototype.selectedRoleCopy = m.action(function(listRoles, tenantId, value) {
    this.copiedRoles = [];
    this.copiedRolePermission = [];

    forEach(
      function(item) {
        item.id === tenantId ? (item.checked = value) : null;
        if(item.checked) {
          this.copiedRoles.push(item.id);
          this.copyRolePermission && this.copiedRolePermission.push(item.id);
        }
      }.bind(this),
      listRoles
    );
    this.markDirty();
  });

  /**
   * Update schedule
   */
  WorkspaceDetail.prototype.setCopySchedule = m.action(function(schedule) {
    this.copySchedule = schedule;
    this.markDirty();
  });

  /**
   * Set Copy Role Permissions
   * @param {any} value
   */
  WorkspaceDetail.prototype.setCopyRolePermissions = m.action(function(value) {
    this.copyRolePermission = value;

    if(this.copyRolePermission){
      forEach(function(item) {
          this.copiedRolePermission.push(item);
        }.bind(this),
        this.copiedRoles
      );
    } else {
      this.copiedRolePermission = [];
    }

    this.markDirty();
  });

  /**
   * reset Value For Run Copy
   */
  WorkspaceDetail.prototype.resetValueForRunCopy = m.action(function() {
    this.copyOption = {};
    this.copyAdvancedSettingOption = {};
    this.copyDashboardOption = {};
    this.copyReportOption = {};
    this.copyTenantPermissionOption = {};
    this.copyRolePermissionOption = {};
  });

  /**
   * set Can Run Copy Status
   * @param  {boolean} state
   */
  WorkspaceDetail.prototype.setCanRunCopyStatus = m.action(function(state) {
    this.canRunCopy = state;
  });

  /**
   * check Has Selected Conflicting Item Or Not
   * @param  {object} options
   */
  WorkspaceDetail.prototype.checkHasSelectedConflictingItem = function(options) {
    return some(function(item) {
      return item === true;
    }, options);
  };

  /**
   * get checked querySources for re-populating sourceConnections when concurrency occurs
   * @return [Array]
   */
  WorkspaceDetail.prototype.getSelectedQuerySources = function() {
    var selectedQuerySources = {};

    var handleQuerySources = function(querySources) {
      forEach(function(querySource) {
        if (!querySource.checked) {
          return;
        }

        selectedQuerySources[querySource.id] = querySource.name;

        var querySources = querySource.dbSource ? querySource.dbSource.querySources : querySource.querySources;
        handleQuerySources(querySources);
      }, querySources);
    };

    handleQuerySources(this.sourceConnections);

    return selectedQuerySources;
  };

  /**
   * re-populate checked querySources of current Workspace when concurrency occurs
   * @param {Array} sourceConnections
   * @param {Array} selectedQuerySources
   */
  WorkspaceDetail.prototype.populateCheckedQuerySources = m.action(function(sourceConnections, selectedQuerySources) {
    var handleQuerySources = function(querySources) {
      forEach(function(querySource) {
        if (selectedQuerySources[querySource.id] === querySource.name) {
          querySource.checked = true;
          var querySources = querySource.dbSource ? querySource.dbSource.querySources : querySource.querySources;
          handleQuerySources(querySources);
        } else {
          return;
        }
      }, querySources);
    };

    handleQuerySources(sourceConnections);

    this.readSourceConnections(sourceConnections);
  });

  /**
   * update value ToObject of tenant after separate mapping from the mainpage
   */
  WorkspaceDetail.prototype.updateMappingData = m.action(function(selectedTenant) {
    selectedTenant = selectedTenant || this.selectedTenant;
    var dataMapping = selectedTenant.dataMapping;

    dataMapping.forEach(
      function(mappingItem) {
        // For Database type : Disabled dropdown and show database alias name
        if (mappingItem.type === constants.MAPPING_TYPE.Database) {
          var selectedDB = this.findSelectedDatabase(mappingItem.fromDatabaseKey);
          if (selectedDB && isEmpty(mappingItem.fromObject)) {
            mappingItem.fromObject = selectedDB.name;
          }

          var selectedToDataSource = find({ fullDatabaseKey: mappingItem.toDatabaseKey }, selectedTenant.databaseSources);
          if (selectedToDataSource && isEmpty(mappingItem.toObject)) {
            mappingItem.toObject = selectedToDataSource.name;
          }
        }
      }.bind(this)
    );
  });

  /**
   * Set source reports to copy
   * @param  {object} options
   */
  WorkspaceDetail.prototype.setHashCodeForReports = m.action(function(reportHashCode, templateHashCode) {
    this.allHashcodes.reports = reportHashCode;
    this.allHashcodes.templates = templateHashCode;
  });

  /**
   * Get source reports previous
   */
  WorkspaceDetail.prototype.getSourceReportsPrev = m.action(function() {
    this.sourceReportsPrev.push({ id: this.sourceReports.id, checked: this.sourceReports.checked });
    forEach(
      function(category) {
        this.sourceReportsPrev.push({ id: category.id, checked: category.checked });
        forEach(
          function(subCategory) {
            this.sourceReportsPrev.push({ id: subCategory.id, checked: subCategory.checked });
            forEach(
              function(report) {
                this.sourceReportsPrev.push({ id: report.id, checked: report.checked });
              }.bind(this),
              subCategory.reports
            );
          }.bind(this),
          category.subCategories
        );
      }.bind(this),
      this.sourceReports.categories
    );
  });

  /**
   * Get source templates previous
   */
  WorkspaceDetail.prototype.getSourceTemplatesPrev = m.action(function() {
    this.sourceTemplatesPrev.push({ id: this.sourceTemplates.id, checked: this.sourceTemplates.checked });
    forEach(
      function(category) {
        this.sourceTemplatesPrev.push({ id: category.id, checked: category.checked });
        forEach(
          function(subCategory) {
            this.sourceTemplatesPrev.push({ id: subCategory.id, checked: subCategory.checked });
            forEach(
              function(report) {
                this.sourceTemplatesPrev.push({ id: report.id, checked: report.checked });
              }.bind(this),
              subCategory.reports
            );
          }.bind(this),
          category.subCategories
        );
      }.bind(this),
      this.sourceTemplates.categories
    );
  });

  /**
   * Get source dashboards previous
   */
  WorkspaceDetail.prototype.getSourceDashboardsPrev = m.action(function() {
    this.sourceDashboardsPrev.push({ id: this.sourceDashboards.id, checked: this.sourceDashboards.checked });
    forEach(
      function(category) {
        this.sourceDashboardsPrev.push({ id: category.id, checked: category.checked });
        forEach(
          function(subCategory) {
            this.sourceDashboardsPrev.push({ id: subCategory.id, checked: subCategory.checked });
            forEach(
              function(dashboard) {
                this.sourceDashboardsPrev.push({ id: dashboard.id, checked: dashboard.checked });
              }.bind(this),
              subCategory.dashboards
            );
          }.bind(this),
          category.subCategories
        );
      }.bind(this),
      this.sourceDashboards.categories
    );
  });

  /**
   * Reset previous data
   */
  WorkspaceDetail.prototype.resetPrevData = m.action(function() {
    this.sourceReportsPrev = [];
    this.sourceTemplatesPrev = [];
  });

  /**
   * Reset previous data
   */
  WorkspaceDetail.prototype.resetDashboardPrevData = m.action(function() {
    this.sourceDashboardsPrev = [];
  });

  /**
   * Reset previous data
   */
  WorkspaceDetail.prototype.resetDataSourceForCopy = m.action(function() {
    this.resetCopyReportSource();
    this.resetCopyDashboardSource();
  });

  /**
   * Update query source
   */
  WorkspaceDetail.prototype.updateQuerySourceTree = m.action(function() {
    var querySources = this.querySourceTree.querySources;
    var checked = this.copyDataModel;
    forEach(function(querySoure) {
      querySoure.checked = checked;
      forEach(function(dbQuerySource) {
        dbQuerySource.checked = checked;
        forEach(function(qs) {
          qs.checked = checked;
          forEach(function(querySourceField) {
            querySourceField.checked = checked;
          }, qs.querySourceFields);
        }, dbQuerySource.querySources);
      }, querySoure.dbSource.querySources);
    }, querySources);
  });

  /**
   * Set hasd code for dashboard
   * @param  {String} hash code
   */
  WorkspaceDetail.prototype.setHashCodeForDashboards = m.action(function(hashCode) {
    this.allHashcodes.dashboards = hashCode;
  });

  /**
   * reset Workspace Tenants status to need validation
   */
  WorkspaceDetail.prototype.resetWorkspaceTenantsStatus = m.action(function(tenant) {
    var selectedTenant = tenant || this.selectedTenant;
    if (selectedTenant) {
      selectedTenant.setDirty();
      selectedTenant.readSourceConnections(this.mapppingDatabasesSource);
    } else {
      this.workspaceTenants.forEach(
        function(tenant) {
          tenant.setDirty();
          tenant.readSourceConnections(this.mapppingDatabasesSource);
        }.bind(this)
      );
    }

    this.alertMessage.message = '';
    this.markDirty();
  });

  WorkspaceDetailCopyReport(WorkspaceDetail);

  module.exports = WorkspaceDetail;
})();



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.WorkspaceDetail.js